import {FC, useCallback, useContext, useMemo, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  ModalProps,
  Chip,
  Box,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {FetchResult, useQuery} from '@apollo/client';
import {
  GET_CATALOGS,
  GET_CONFIGURATIONS,
  FILTER_USERS,
  GET_MONETIZATION_CONFIGURATIONS,
} from 'graphql/absences/Querys';
import Modal from 'components/Modal/Modal';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import GqlFormController from 'controllers/GqlFormController/GqlFormController';
import {CREATE_ADDITIONAL_LEAVE_BATCH} from 'graphql/absences/Mutations';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import {Catalog} from 'models/catalog';
import {FormControllerControlProps} from 'controllers/FormController/FormController';
import {useLogger} from 'hooks/useLogger';
import {alertContext} from 'contextApi/context/error';
import {formatDate} from 'commons/dates';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {authContext} from 'contextApi/context/auth';
import {useTranslateCatalog} from 'commons/hooks/useTranslateCatalog';

export type SelectionMode = 'PERSON' | 'COUNTRY';

interface AdditionalLeaveCreateModalProps extends Omit<ModalProps, 'children'> {
  selectionMode?: SelectionMode;
  onSelectionModeChange: (mode: SelectionMode) => void;
}

interface AdditionalLeaveFormValues {
  additionalLeaveType: string;
  date: {startDate: Date};
  expirationDate: {startDate: Date};
  days: string;
  comments: string;
}

interface SelectedPalowan {
  id: string;
  firstName: string;
  lastName: string;
  country: string;
}

interface MonetizationConfig {
  id: string;
  country: string;
  leaveType: Catalog;
  days: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

function validateStartDate(value: unknown, errorMsg: string): true | string {
  const v = value as {startDate?: Date} | null;
  return !!v?.startDate || errorMsg;
}

function validateExpirationDate(
  value: unknown,
  watchedDate: unknown,
  errorRequired: string,
  errorInvalid: string
): true | string {
  const v = value as {startDate?: Date} | null;
  if (!v?.startDate) return errorRequired;
  const startDate =
    watchedDate instanceof Date
      ? watchedDate
      : (watchedDate as {startDate?: Date} | null)?.startDate;
  if (!startDate || v.startDate <= startDate) return errorInvalid;
  return true;
}

const AdditionalLeaveCreateModal: FC<AdditionalLeaveCreateModalProps> = ({
  onClose,
  selectionMode,
  onSelectionModeChange,
  ...props
}) => {
  const logger = useLogger(AdditionalLeaveCreateModal.name);
  const {userDetails} = useContext(authContext);
  const {dispatch} = useContext(alertContext);
  const {t} = useTranslation();
  const {translateCatalogs} = useTranslateCatalog();
  const [selectedUsers, setSelectedUsers] = useState<SelectedPalowan[]>([]);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState<boolean>(false);
  const [confirmModeChangeOpen, setConfirmModeChangeOpen] =
    useState<boolean>(false);
  const [pendingMode, setPendingMode] = useState<SelectionMode | null>(null);

  // ADL-004: country selection state
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // ADL-008: tracks in-flight batch mutation to disable submit during request
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ADL-007: watch date field to enable cross-field validation for expirationDate
  const [watchedDate, setWatchedDate] = useState<Date | null>(dayjs().toDate());

  // Stable initial form values — must be memoized to avoid re-triggering
  // FormController's defaultValues useEffect on every parent re-render.
  const formDefaultValues = useMemo(
    () => ({
      date: dayjs().toDate(),
      expirationDate: dayjs().add(2, 'months').toDate(),
      days: 1,
      comments: '',
    }),
    []
  );

  // Stable watch callback — must be useCallback to avoid triggering
  // FormController's watch subscription on every render.
  const handleFormWatch = useCallback((values: Record<string, unknown>) => {
    const dateValue = values.date as {startDate?: Date} | Date | null;
    setWatchedDate(
      dateValue instanceof Date
        ? dateValue
        : (dateValue as {startDate?: Date} | null)?.startDate ?? null
    );
  }, []);

  const {data: configurationsData, loading: loadingConfigurations} = useQuery(
    GET_CONFIGURATIONS,
    {skip: selectionMode !== 'COUNTRY'}
  );

  const configurations: Array<{country: string}> = useMemo(
    () => configurationsData?.getConfigurations ?? [],
    [configurationsData]
  );

  // Single query for all countries — groups by country client-side.
  // One reactive useQuery is reliably testable with MockedProvider.
  const countryList = useMemo(
    () => configurations.map(c => c.country),
    [configurations]
  );
  const {data: allUsersData, loading: loadingAllUsers} = useQuery(
    FILTER_USERS,
    {
      variables: {countries: countryList},
      skip: selectionMode !== 'COUNTRY' || countryList.length === 0,
    }
  );

  const countryActiveUsersMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!allUsersData?.filterUsers) return map;
    for (const cfg of configurations) {
      map[cfg.country] = 0;
    }
    for (const user of allUsersData.filterUsers as Array<{country: string}>) {
      if (map[user.country] !== undefined) {
        map[user.country]++;
      }
    }
    return map;
  }, [allUsersData, configurations]);

  const isLoadingCountries =
    selectionMode === 'COUNTRY' && (loadingConfigurations || loadingAllUsers);

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent<string>): void => {
      setSelectedCountry(event.target.value || null);
    },
    []
  );

  const handleCloseAttempt = useCallback(
    (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (confirmModeChangeOpen) {
        setConfirmModeChangeOpen(false);
        setPendingMode(null);
      }
      if (selectedUsers.length > 0) {
        setConfirmCloseOpen(true);
      } else {
        setSelectedCountry(null);
        onClose && onClose(event, reason);
      }
    },
    [selectedUsers, onClose, confirmModeChangeOpen]
  );

  const handleModeChangeRequest = useCallback(
    (newMode: SelectionMode): void => {
      if (confirmModeChangeOpen) {
        setConfirmModeChangeOpen(false);
        setPendingMode(null);
      }
      const hasActiveSelection =
        selectionMode === 'PERSON'
          ? selectedUsers.length > 0
          : selectedCountry !== null;
      if (hasActiveSelection) {
        setPendingMode(newMode);
        setConfirmModeChangeOpen(true);
      } else {
        onSelectionModeChange(newMode);
      }
    },
    [
      confirmModeChangeOpen,
      selectionMode,
      selectedUsers.length,
      selectedCountry,
      onSelectionModeChange,
    ]
  );

  // Create a Set of selected IDs to avoid recreating transformResult on every user selection
  const selectedUserIds = useMemo(
    () => new Set(selectedUsers.map(u => u.id)),
    [selectedUsers]
  );

  // Keep maxUsersReached stable to avoid infinite re-renders
  const maxUsersReached = selectedUsers.length >= 10;
  const selectedUsersCountry = selectedUsers[0]?.country ?? null;
  const catalogCountry = useMemo(
    () =>
      selectionMode === 'COUNTRY' ? selectedCountry : selectedUsersCountry,
    [selectionMode, selectedCountry, selectedUsersCountry]
  );

  const transformResult = useCallback(
    (item: SelectedPalowan) => {
      const isAlreadySelected = selectedUserIds.has(item.id);
      const hasDifferentCountry =
        !!selectedUsersCountry && item.country !== selectedUsersCountry;

      return {
        id: item.id,
        text: `${item.firstName} ${item.lastName}${
          item.country ? ` — ${item.country}` : ''
        }`,
        disabled:
          (!isAlreadySelected && maxUsersReached) ||
          (!isAlreadySelected && hasDifferentCountry),
      };
    },
    [maxUsersReached, selectedUserIds, selectedUsersCountry]
  );

  const handleResultSelect = useCallback(
    (palowan: SelectedPalowan): void => {
      if (selectedUsers.some(u => u.id === palowan.id)) {
        logger.debug('Duplicate user selected, ignoring');
        return;
      }
      if (selectedUsers.length >= 10) {
        logger.debug('Limit of 10 users reached, cannot add more');
        return;
      }
      if (selectedUsersCountry && palowan.country !== selectedUsersCountry) {
        logger.debug('User from different country selected, ignoring');
        return;
      }
      setSelectedUsers([
        ...selectedUsers,
        {
          id: palowan.id,
          firstName: palowan.firstName,
          lastName: palowan.lastName,
          country: palowan.country,
        },
      ]);
    },
    [selectedUsers, logger, selectedUsersCountry]
  );

  const handleRemoveUser = useCallback(
    (userId: string): void => {
      setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
    },
    [selectedUsers]
  );

  const handleToggleChange = useCallback(
    (_, value: SelectionMode | null) => {
      if (value) {
        handleModeChangeRequest(value);
      }
    },
    [handleModeChangeRequest]
  );

  const handleBatchSubmit = useCallback(
    (mutationPromise: Promise<FetchResult<GQLValue>>) => {
      setIsSubmitting(true);
      mutationPromise
        .then(result => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const batchResult = (result.data as any)?.createAdditionalLeaveBatch;
          if (
            batchResult.createdCount === 0 &&
            (!batchResult.errors || batchResult.errors.length === 0)
          ) {
            dispatch({
              type: 'show',
              payload: {
                message: t(
                  'leaves.AdditionalLeaveCreateModal.batchNoActiveUsersMessage'
                ),
                level: 'warning',
                duration: 'short',
                cancelable: true,
              },
            });
          } else if (!batchResult.errors || batchResult.errors.length === 0) {
            location.reload();
          } else {
            setSelectedUsers(prev =>
              prev.filter(u => !batchResult.affectedUserIds.includes(u.id))
            );
            dispatch({
              type: 'show',
              payload: {
                message: t(
                  'leaves.AdditionalLeaveCreateModal.batchPartialMessage',
                  {
                    created: batchResult.createdCount,
                    failed: batchResult.errors.length,
                  }
                ),
                level: 'warning',
                duration: 'short',
                cancelable: true,
              },
            });
          }
        })
        .catch(() => {
          dispatch({
            type: 'show',
            payload: {
              message: t('leaves.AdditionalLeaveCreateModal.errorMessage'),
              level: 'error',
              duration: 'short',
              cancelable: true,
            },
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, t, setIsSubmitting, setSelectedUsers]
  );

  const buildMapper = useCallback(
    (mergedLeaveTypes: Catalog[]) => (values: GQLValue) => {
      const formValues = values as unknown as AdditionalLeaveFormValues;
      const type = formValues.additionalLeaveType;
      const initialDate = formatDate(formValues.date.startDate);
      const expirationDate = formatDate(formValues.expirationDate.startDate);
      const days = Number(formValues.days);
      const description = formValues.comments;
      void mergedLeaveTypes; // consumed via controls; kept for reference parity
      if (selectionMode === 'COUNTRY') {
        return {
          targetMode: 'BY_COUNTRY',
          countryCode: selectedCountry,
          type,
          initialDate,
          expirationDate,
          days,
          description,
        };
      }
      return {
        targetMode: 'BY_USERS',
        userIds: selectedUsers.map(u => u.id),
        type,
        initialDate,
        expirationDate,
        days,
        description,
      };
    },
    [selectionMode, selectedCountry, selectedUsers]
  );

  return (
    <Modal
      title={t('leaves.AdditionalLeaveCreateModal.title')}
      description={t('leaves.AdditionalLeaveCreateModal.description')}
      {...props}
      onClose={handleCloseAttempt}
    >
      <Box sx={{mb: 3}}>
        <ToggleButtonGroup
          exclusive={true}
          value={selectionMode}
          onChange={handleToggleChange}
        >
          <ToggleButton value="PERSON">
            {t('leaves.AdditionalLeaveCreateModal.togglePersonLabel')}
          </ToggleButton>
          <ToggleButton value="COUNTRY">
            {t('leaves.AdditionalLeaveCreateModal.toggleCountryLabel')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {selectionMode === 'COUNTRY' ? (
        // ADL-004: country selection mode
        <>
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{mb: 1}}>
              {t('leaves.AdditionalLeaveCreateModal.countryLabel')}
            </Typography>
            <Select
              value={selectedCountry ?? ''}
              onChange={handleCountryChange}
              displayEmpty
              disabled={isLoadingCountries}
              fullWidth
            >
              <MenuItem value="">
                <em>
                  {t('leaves.AdditionalLeaveCreateModal.countryEmptyState')}
                </em>
              </MenuItem>
              {Object.entries(countryActiveUsersMap)
                .filter(([, count]) => count > 0)
                .map(([code]) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
            </Select>
          </Box>

          {selectedCountry !== null && (
            <Box sx={{mb: 3}}>
              <Typography variant="body2" color="text.secondary">
                {t('leaves.AdditionalLeaveCreateModal.countryActiveUsersInfo', {
                  count: countryActiveUsersMap[selectedCountry] ?? 0,
                })}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        // Person selection mode (ADL-001 / ADL-002)
        <>
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{mb: 1}}>
              {t('leaves.AdditionalLeaveCreateModal.searchByName')}
            </Typography>
            <SearchBarController
              query={FILTER_USERS}
              queryVariableName="keyword"
              placeholder={t('leaves.AdditionalLeaveCreateModal.searchByName')}
              transformResult={transformResult}
              onResultSelect={handleResultSelect}
            />
            {selectedUsers.length === 10 && (
              <Typography variant="body2" color="warning.main" sx={{mt: 1}}>
                {t('leaves.AdditionalLeaveCreateModal.maxUsersReached')}
              </Typography>
            )}
          </Box>

          {selectedUsers.length > 0 && (
            <Box sx={{mb: 3}}>
              <Typography variant="subtitle2" sx={{mb: 1}}>
                {t('leaves.AdditionalLeaveCreateModal.selectedPalowansLabel')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                {t(
                  'leaves.AdditionalLeaveCreateModal.catalogReferenceCountryInfo',
                  {
                    country: selectedUsersCountry,
                  }
                )}
              </Typography>
              <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                {selectedUsers.map(user => (
                  <Chip
                    key={user.id}
                    label={`${user.firstName} ${user.lastName}`}
                    onDelete={() => handleRemoveUser(user.id)}
                    data-testid={`user-chip-${user.id}`}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
      <GqlQueryController
        key={`additional-leave-types-${catalogCountry ?? 'none'}`}
        initialState={[] as Catalog[]}
        QUERY={GET_CATALOGS}
        variables={{
          type: 'ADDITIONAL_LEAVE_TYPE',
          country: catalogCountry,
        }}
        paramValidator={(_, current) => !!current?.country}
        rootField={'catalogs'}
      >
        {({data: additionalLeaveTypes}) => (
          <GqlQueryController
            QUERY={GET_MONETIZATION_CONFIGURATIONS}
            variables={{
              country: userDetails?.data?.country,
              status: 'ACTIVE',
            }}
            initialState={{data: []}}
            rootField="getMonetizationConfigurations"
          >
            {({data: monetizationConfig}) => {
              const monetizationLeaveTypes: Catalog[] = (
                monetizationConfig?.data || []
              )
                .map((config: MonetizationConfig) => config.leaveType)
                .filter(Boolean)
                .filter(
                  (lt: Catalog, index: number, self: Catalog[]) =>
                    index === self.findIndex((t: Catalog) => t.id === lt.id)
                );

              const existingIds = new Set(
                additionalLeaveTypes.map((lt: Catalog) => lt.id)
              );
              const mergedLeaveTypesLocal: Catalog[] = [
                ...additionalLeaveTypes,
                ...monetizationLeaveTypes.filter(
                  (lt: Catalog) => !existingIds.has(lt.id)
                ),
              ];

              // Compute translated leave types directly without state
              const translatedLeaveTypes = translateCatalogs(
                mergedLeaveTypesLocal
              );

              const controls: FormControllerControlProps[] = [
                {
                  name: 'additionalLeaveType',
                  type: 'select',
                  options: {
                    required: t(
                      'leaves.AdditionalLeaveCreateModal.errorRequiredType'
                    ),
                  },
                  props: {
                    label: t('leaves.AdditionalLeaveCreateModal.selectLabel'),
                    placeholder: t(
                      'leaves.AdditionalLeaveCreateModal.selectLabel'
                    ),
                    disabled: !catalogCountry,
                    children: (translatedLeaveTypes || []).map(
                      (type: Catalog) => ({
                        key: type.name,
                        description: type.description,
                      })
                    ),
                  },
                },
                {
                  name: 'days',
                  options: {
                    validate: (value: unknown) =>
                      Number(value) > 0 ||
                      t('leaves.AdditionalLeaveCreateModal.errorDaysInvalid'),
                  },
                  props: {
                    label: t('leaves.AdditionalLeaveCreateModal.days'),
                    type: 'number',
                    inputProps: {step: 1, min: 1},
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t('leaves.AdditionalLeaveCreateModal.daysSuffix')}
                        </InputAdornment>
                      ),
                    },
                    'data-testid': 'days-input',
                  },
                },
                {
                  name: 'date',
                  type: 'date',
                  options: {
                    validate: (value: unknown) =>
                      validateStartDate(
                        value,
                        t(
                          'leaves.AdditionalLeaveCreateModal.errorStartDateRequired'
                        )
                      ),
                  },
                  props: {
                    label: t('leaves.AdditionalLeaveCreateModal.startDate'),
                    selectionType: 'single',
                  },
                },
                {
                  name: 'expirationDate',
                  type: 'date',
                  options: {
                    validate: (value: unknown) =>
                      validateExpirationDate(
                        value,
                        watchedDate,
                        t(
                          'leaves.AdditionalLeaveCreateModal.errorExpirationDateRequired'
                        ),
                        t(
                          'leaves.AdditionalLeaveCreateModal.errorExpirationDateInvalid'
                        )
                      ),
                  },
                  props: {
                    label: t(
                      'leaves.AdditionalLeaveCreateModal.expirationDate'
                    ),
                    selectionType: 'single',
                  },
                },
                {
                  name: 'comments',
                  props: {
                    placeholder: t(
                      'leaves.AdditionalLeaveCreateModal.commentsLabel'
                    ),
                    label: t('leaves.AdditionalLeaveCreateModal.commentsLabel'),
                  },
                },
              ];

              const handleOnSubmit = (
                mutationPromise: Promise<FetchResult<GQLValue>>,
                data: Map<string, GQLValue> | undefined
              ) => {
                logger.debug('on submit', data);
                handleBatchSubmit(mutationPromise);
              };

              const mapper = buildMapper(mergedLeaveTypesLocal);

              return (
                <>
                  <GqlFormController
                    className="flex flex-col"
                    itemProps={{xs: 12}}
                    mutation={CREATE_ADDITIONAL_LEAVE_BATCH}
                    variablesMapper={mapper}
                    defaultValues={formDefaultValues}
                    controls={controls}
                    onSubmit={handleOnSubmit}
                    watch={handleFormWatch}
                  >
                    {({onSubmit}) => (
                      <div>
                        <Button
                          onClick={() =>
                            handleCloseAttempt({}, 'backdropClick')
                          }
                          variant="outlined"
                        >
                          {t('leaves.AdditionalLeaveCreateModal.exit')}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onSubmit()}
                          disabled={
                            selectionMode === 'COUNTRY'
                              ? selectedCountry === null || isSubmitting
                              : selectedUsers.length === 0 || isSubmitting
                          }
                        >
                          {t('leaves.AdditionalLeaveCreateModal.submit')}
                        </Button>
                      </div>
                    )}
                  </GqlFormController>

                  <Dialog open={confirmCloseOpen}>
                    <DialogTitle>
                      {t('leaves.AdditionalLeaveCreateModal.confirmCloseTitle')}
                    </DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        {t(
                          'leaves.AdditionalLeaveCreateModal.confirmCloseMessage'
                        )}
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        variant="outlined"
                        onClick={() => setConfirmCloseOpen(false)}
                      >
                        {t(
                          'leaves.AdditionalLeaveCreateModal.confirmCloseCancel'
                        )}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setSelectedUsers([]);
                          setConfirmCloseOpen(false);
                          onClose && onClose({}, 'backdropClick');
                        }}
                      >
                        {t(
                          'leaves.AdditionalLeaveCreateModal.confirmCloseConfirm'
                        )}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog open={confirmModeChangeOpen}>
                    <DialogTitle>
                      {t(
                        'leaves.AdditionalLeaveCreateModal.confirmModeChangeTitle'
                      )}
                    </DialogTitle>
                    <DialogActions>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setConfirmModeChangeOpen(false);
                          setPendingMode(null);
                        }}
                      >
                        {t(
                          'leaves.AdditionalLeaveCreateModal.confirmModeChangeCancel'
                        )}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setSelectedUsers([]);
                          setSelectedCountry(null);
                          onSelectionModeChange(pendingMode!);
                          setConfirmModeChangeOpen(false);
                          setPendingMode(null);
                        }}
                      >
                        {t(
                          'leaves.AdditionalLeaveCreateModal.confirmModeChangeConfirm'
                        )}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              );
            }}
          </GqlQueryController>
        )}
      </GqlQueryController>
    </Modal>
  );
};

export default AdditionalLeaveCreateModal;
